'use client';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useIsPwa } from '../context/pwaContext';

const siteUrl = (process.env['NEXT_PUBLIC_SITE_URL'] || '').replace(/\/$/, '');

// Set on staging, which serves the same content as production and would otherwise be indexed
// alongside it. NEXT_PUBLIC_* is inlined at build time, so changing it needs a redeploy.
const noindexEnv = ['true', '1'].includes((process.env['NEXT_PUBLIC_NOINDEX'] || '').toLowerCase());

type PageHeadProps = {
    image?: string | null;
    defaultTitle: string;
    title?: string | null;
    description?: string | null;
    keywords?: string | null;
    noindex?: boolean;
    preloadImage?: string;
};

export const PageHead = (props: PageHeadProps) => {
    const { image, title = '', description = '', keywords = '', noindex = false, defaultTitle, preloadImage } = props;
    const isPwa = useIsPwa();
    const { asPath } = useRouter();

    // The share crawlers (facebookexternalhit, Twitterbot) don't run JS, so og:url has to be in the
    // server rendered html - reading window.location in an effect leaves it empty for them. Only
    // falls back to the browser when NEXT_PUBLIC_SITE_URL isn't set (local dev, deploy previews).
    const [browserUrl, setBrowserUrl] = useState<string>('');
    const canonicalPath = asPath.split('#')[0].split('?')[0];
    const pageUrl = siteUrl ? `${siteUrl}${canonicalPath}` : browserUrl;

    useEffect(() => {
        setBrowserUrl(`${window.location.origin}${window.location.pathname}`);
    }, [canonicalPath]);

    const pageTitle = title === null ? '' : `${title} | `;
    const fullTitle = `${pageTitle}${defaultTitle}`;

    // the pwa is never indexed: it is an on site app, not a public page
    const hideFromSearch = noindex || noindexEnv || isPwa;

    return (
        <Head>
            {isPwa && <link rel="manifest" href={`/manifest.json`}></link>}
            <meta charSet="utf-8" />
            <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
            <meta
                name="viewport"
                content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, viewport-fit=cover"
            />
            <title>{fullTitle}</title>
            {hideFromSearch && <meta name="robots" content="noindex,nofollow" key="robots" />}
            {description && <meta name="description" content={description} key="description" />}
            {keywords && <meta name="keywords" content={keywords} />}
            {preloadImage && <link rel="preload" href={preloadImage} as="image" />}
            <link rel="shortcut icon" href={`/favicon.ico`} />

            {isPwa && (
                <>
                    <link href={`/icons/icon-16.png`} rel="icon" type="image/png" sizes="16x16" />
                    <link href={`/icons/icon-32.png`} rel="icon" type="image/png" sizes="32x32" />
                    {/* Single opaque 180x180 icon. iOS composites transparency onto
                        black and only reads apple-touch-icon (not the manifest icons),
                        so we point at the flattened /apple-touch-icon.png. iOS also
                        auto-probes that root path when adding to the Home Screen. */}
                    <link rel="apple-touch-icon" href={`/apple-touch-icon.png`} />
                    <link rel="mask-icon" href={`/icons/maskable_icon_x512.png`} color="#4C719B" />
                    <meta name="application-name" content={defaultTitle} />
                    <meta name="apple-mobile-web-app-capable" content="yes" />
                    <meta name="apple-mobile-web-app-status-bar-style" content="default" />
                    <meta name="apple-mobile-web-app-title" content={defaultTitle} />

                    <meta name="format-detection" content="telephone=no" />
                    <meta name="mobile-web-app-capable" content="yes" />
                    <meta name="msapplication-config" content={`/icons/browserconfig.xml`} />
                    <meta name="msapplication-TileColor" content="#4C719B" />
                    <meta name="msapplication-tap-highlight" content="no" />
                    <meta name="theme-color" content="#172535" />
                </>
            )}

            {/* a card without an image renders as a small summary, so only ask for the big one when there is one */}
            <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
            {/* only when known: crawlers fall back to the url they fetched, an empty one just misleads them */}
            {pageUrl && <meta name="twitter:url" content={pageUrl} />}
            <meta name="twitter:title" content={title || defaultTitle} />
            {description && <meta name="twitter:description" content={description} />}
            {image && <meta name="twitter:image" content={image} />}

            <meta property="og:site_name" content={defaultTitle} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={title || defaultTitle} />
            {description && <meta property="og:description" content={description} />}
            {pageUrl && <meta property="og:url" content={pageUrl} />}
            {image && <meta property="og:image" content={image} />}
        </Head>
    );
};
